import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { 
    setCustomFilters, setCustomFiltersApplied, setVersion, setEditMode, 
    setRunListDataSearched, setSearchTerm, getRunDataAllGroupedAsync, 
    setNumberOfCustomFiltersApplied, setJWT, setRunListDataFiltered, 
    setJWTExp, logOutCurrentUser, resetState, getRunInfoLengthData, 
    getDiskSpacData, calculcateStorageFreeSpace 
} from "../actions/index";
import Root from '../root/root';
import * as Utils from '../../utils/utils';

const RootContainer = () => {
    const [runData, setRunData] = useState([]);
    const state = useSelector(state => state);
    const dispatch = useDispatch();
    const workerRef = useRef(null);

    const redirect = () => window.location.href = process.env.REACT_APP_API_URL_AUTH_LOGOUT;

    // Initialization & API Fetching   
    useEffect(() => {
        if (process.env.REACT_APP_VERSION) {
            dispatch(setVersion(process.env.REACT_APP_VERSION));
        }
        
        if (state.runlist.length === 0 && !state.is_run_data_loading) {
            dispatch(getRunDataAllGroupedAsync(100, state.jwt));
            dispatch(getRunInfoLengthData(state.jwt));
            dispatch(getDiskSpacData(state.jwt));
            dispatch(calculcateStorageFreeSpace(state.custom_storage, state.jwt));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Session & Metadata
    useEffect(() => {
        const title = state.runId ? `Nexus - ${state.is_unified_row_selected ? Utils.getRunIdWithoutCamera(state.runId) : state.runId}` : `Nexus`;
        document.title = title;

        if (state.jwt_exp && Date.now() >= state.jwt_exp) {
            dispatch(logOutCurrentUser());
            dispatch(resetState());
            redirect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.runId, state.is_unified_row_selected, state.jwt_exp, dispatch]);

    // Data Processing
    useEffect(() => {
        if (!state.runlist || state.runlist.length === 0) return;

        // Clean up previous worker to prevent race conditions
        if (workerRef.current) workerRef.current.terminate();

        const worker = new Worker(new URL('../../workers/runlist.worker.js', import.meta.url));
        workerRef.current = worker;

        worker.postMessage({ runlist: state.runlist });

        worker.onmessage = (e) => {
            const processed = e.data;
            
            if (state.is_custom_filters_applied) {
                const filtered = Utils.filterByCustomFilters(
                    state.custom_filters, 
                    processed, 
                    dispatch, 
                    setNumberOfCustomFiltersApplied
                );
                if (filtered) dispatch(setRunListDataFiltered(filtered));
            }
            else {
                setRunData(Utils.getRunList(processed));
            }
        };

        return () => worker.terminate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.runlist, state.is_custom_filters_applied, dispatch]);

    // View Manager   
    useEffect(() => {
        if (state.runlist_searched?.length > 0) {
            setRunData(state.runlist_searched);
        } 
        else if (state.is_custom_filters_applied && state.runlist_filtered?.length > 0) {
            setRunData(Utils.getRunList(state.runlist_filtered));
        } 
        else if (state.runlist?.length > 0) {
            setRunData(Utils.getRunList(state.runlist));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.runlist_searched, state.runlist_filtered, state.is_custom_filters_applied, state.runlist]);

    // Reset on Edit Save
    useEffect(() => {
        if (state.is_details_data_saved || state.is_analysis_comments_data_saved) {
            dispatch(setRunListDataSearched([]));
            dispatch(setSearchTerm(undefined, 'all'));
            if (state.is_in_edit_mode) dispatch(setEditMode(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.is_details_data_saved, state.is_analysis_comments_data_saved, dispatch]);

    const handleClearAdvancedSearch = useCallback(() => {
        dispatch(setCustomFiltersApplied(false));
        let f = Utils.getColumnFilters();
        f.analysisstatus.series = f.analysisstatus.default;
        f.analysisstatus.op = 'a';
        dispatch(setCustomFilters(f));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    return (
        <Root mainTableData={runData} handleClearAdvancedSearch={handleClearAdvancedSearch} />
    );
};

export default RootContainer;